(function () {
    "use strict";

    /**
    * Course Comparison System
    * Allows users to select and compare up to N courses
    * Uses localStorage for persistence across page reloads
    */

    // ======================
    // Configuration defaults
    // ======================
    const CONFIG = {
        STORAGE_KEY: 'qld_course_comparison',
        MAX_COURSES: 4, // Maximum number of courses that can be compared
        courseRouteBaseUrl: window.NSWP?.urls?.courseRouteBase || `${window.location.origin}/find-training/courses/`,
        availableText: 'Yes',
        notAvailableText: 'No',
        mayBeAvailableText: 'May be available',
        courseDetailsLinkText: 'Read more',
        removeNoteText: 'Remove an item to add this course',
        tableStyling: 'qld__table--striped-columns',
        extendedIconsUrl: window.NSWP?.urls?.extendedIcons || null,
        removeNoteToggleTipId: 'remove-note',
        flyToDuration: 1500
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeFlyToAnimation = null;
    let activeFlyToGhost = null;

    
    // ============================================================================
    // State Manager
    // ============================================================================

    const StateManager = {
        domElements: {
            comparisonTableContainer: null, //data-role="course-compare-table"
            comparisonDrawerContainer: null, //data-role="course-compare-bar"
            toggleButtons: null, //data-role="course-toggle"
            clearButton: null, //data-role="clear-comparison"
            courseList: null, //data-role="course-compare-list"
            countElement: null, //data-role="course-compare-count"
            maxElement: null //data-role="course-compare-max"
        },
        
        /**
         * Initialize DOM element references
         */
        initializeDomReferences() {
            this.domElements.comparisonTableContainer = document.querySelector('[data-role="course-compare-table"]');
            this.domElements.comparisonDrawerContainer = document.querySelector('[data-role="course-compare-bar"]');
            this.domElements.toggleButtons = document.querySelectorAll('[data-role="course-toggle"]');
            this.domElements.clearButton = document.querySelector('[data-role="clear-comparison"]');
            
            if (this.domElements.comparisonDrawerContainer) {
                this.domElements.courseList = this.domElements.comparisonDrawerContainer.querySelector('[data-role="course-compare-list"]');
                this.domElements.countElement = this.domElements.comparisonDrawerContainer.querySelector('[data-role="course-compare-count"]');
                this.domElements.maxElement = this.domElements.comparisonDrawerContainer.querySelector('[data-role="course-compare-max"]');
            }
        },

        /**
         * Load configuration from HTML data attributes
         */
        loadConfigFromHtml() {
            const { comparisonTableContainer } = this.domElements;
            if (!comparisonTableContainer) return;

            // Override CONFIG values from data attributes if specified
            if (comparisonTableContainer.dataset.availableText) {
                CONFIG.availableText = comparisonTableContainer.dataset.availableText;
            }
            
            if (comparisonTableContainer.dataset.notAvailableText) {
                CONFIG.notAvailableText = comparisonTableContainer.dataset.notAvailableText;
            }

            if (comparisonTableContainer.dataset.mayBeAvailableText) {
                CONFIG.mayBeAvailableText = comparisonTableContainer.dataset.mayBeAvailableText;
            }
            
            if (comparisonTableContainer.dataset.courseDetailsLinkText) {
                CONFIG.courseDetailsLinkText = comparisonTableContainer.dataset.courseDetailsLinkText;
            }
            
            if (comparisonTableContainer.dataset.tableStyling) {
                CONFIG.tableStyling = comparisonTableContainer.dataset.tableStyling
                    .split(';')
                    .map(cls => cls.trim())
                    .filter(cls => cls.length > 0)
                    .join(' ');
            }
        },

        /**
         * Initialize the StateManager
         */
        init() {
            this.initializeDomReferences();
            this.loadConfigFromHtml();
        }
    };

    // ======================
    // Data Models
    // ======================

    /**
     * Course entity
     * @typedef {Object} Course
     * @property {string} id - Unique course identifier
     * @property {string} code - Course code
     * @property {string} name - Course title
     * @property {boolean} funded - Whether course is funded
     * @property {boolean} partTime - Part-time availability
     * @property {boolean} online - Online availability
     * @property {boolean} training - Training availability
     * @property {number} minFee - Minimum fee
     * @property {number} maxFee - Maximum fee
     */

    /**
     * Comparison object
     * @typedef {Object} Comparison
     * @property {string[]} courseIds - Array of selected course IDs for quick reference
     * @property {Object.<string, Course>} courses - Course data indexed by ID
     * @property {number} timestamp - Last update timestamp
     */

    // ======================
    // Storage Manager
    // ======================
    const StorageManager = {
        _cache: null,

        /**
         * Get comparison data (cached)
         * @returns {Comparison}
         */
        getComparison() {
            if (this._cache) return this._cache;
            try {
                const data = localStorage.getItem(CONFIG.STORAGE_KEY);
                this._cache = data ? JSON.parse(data) : { courseIds: [], courses: {}, timestamp: Date.now() };
            } catch (error) {
                console.error('Error reading from localStorage:', error);
                this._cache = { courseIds: [], courses: {}, timestamp: Date.now() };
            }
            return this._cache;
        },

        /**
         * Save comparison data
         * @param {Comparison} comparison
         */
        setComparison(comparison) {
            try {
                comparison.timestamp = Date.now();
                this._cache = comparison; // update cache first
                localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(comparison));
                this.dispatchComparisonChanged();
            } catch (error) {
                console.error('Error writing to localStorage:', error);
            }
        },

        /**
         * Clear all comparison data
         */
        clearComparison() {
            try {
                this._cache = { courseIds: [], courses: {}, timestamp: Date.now() };
                localStorage.removeItem(CONFIG.STORAGE_KEY);
                this.dispatchComparisonChanged();
            } catch (error) {
                console.error('Error clearing localStorage:', error);
            }
        },

        invalidateCache() {
            this._cache = null;
        },

        /**
         * Dispatch custom event when comparison changes
         */
        dispatchComparisonChanged() {
            const event = new CustomEvent('comparisonChanged', {
                detail: {
                    comparison: this.getComparison(),
                    timestamp: Date.now()
                }
            });
            window.dispatchEvent(event);
        }
    };


    // ======================
    // Course Comparison Manager
    // ======================
    const ComparisonManager = {
        initialTableHTML: null,

        componentName: "qld__compare-toggle",
        toggleTipTriggerQueryClass: ".qld__compare-toggle-trigger",
        toggleTipContentQueryClass: ".qld__compare-toggle-content",
        marginFromTrigger: 8,

        getToggleTip() {
            return document.getElementById(CONFIG.removeNoteToggleTipId);
        },

        /**
         * Extract course data from button element
         * @param {HTMLElement} button
         * @returns {Course}
         */
        extractCourseData(button) {
            return {
                id: button.dataset.id,
                code: button.dataset.code,
                name: button.dataset.name,
                summaryDuration: button.dataset.summaryDuration || '',
                training: button.dataset.training === 'true',
                financialHelp: button.dataset.financialHelp === 'true',
                entryRequirements: button.dataset.entryRequirements === 'true',
                online: button.dataset.online === 'true',
                fullyOnline: button.dataset.fullyOnline === 'true',
                partiallyOnline: button.dataset.partiallyOnline === 'true',
                partTime: button.dataset.partTime === 'true',
                workplaceBased: button.dataset.workplaceBased === 'true',
                classroomLearning: button.dataset.classroomLearning === 'true',
                practicalLearning: button.dataset.practicalLearning === 'true',
                classroomBased: button.dataset.classroomBased === 'true',
                minFee: button.dataset.minFee || '',
                maxFee: button.dataset.maxFee || '',
                occupationPrimary: button.dataset.occupationPrimary || ''
            };
        },

        closeAllToggleTips() {
            const toggleTipTriggers = document.querySelectorAll(this.toggleTipTriggerQueryClass+'-active');
            toggleTipTriggers.forEach(trigger => {
                QLD.toggleToolTips.closeToggleToolTip(this.componentName, this.getToggleTip(), trigger);
            });
        },
        
        /**
         * Add course to comparison
         * @param {Course} course
         * @param {HTMLElement} button - The button that triggered the add
         * @returns {boolean} Success status
         */
        addCourse(course, button = null) {
            const comparison = StorageManager.getComparison();

            // Check if already added
            if (comparison.courseIds.includes(course.id)) {
                this.showNotification(`${course.name} is already in comparison`, 'info');
                return false;
            }

            // Check max limit
            if (this.getCount() >= CONFIG.MAX_COURSES) {
                const toggleTip = this.getToggleTip();
                const toggleTipTrigger = button;
                QLD.toggleToolTips.openToggleToolTip(this.componentName, toggleTip, toggleTipTrigger, this.marginFromTrigger);

                this.showNotification(`Maximum ${CONFIG.MAX_COURSES} courses can be compared`, 'warning');
                return false;
            }

            // Add course
            comparison.courseIds.push(course.id);
            comparison.courses[course.id] = {
                ...course,
                timestamp: Date.now()
            };
            StorageManager.setComparison(comparison);

            this.showNotification(`${course.name} added to comparison`, 'success');
            return true;
        },

        /**
         * Remove course from comparison
         * @param {string} courseId
         */
        removeCourse(courseId) {
            const comparison = StorageManager.getComparison();

            if (!comparison.courseIds.includes(courseId)) return;

            const removedCourse = comparison.courses[courseId];

            comparison.courseIds = comparison.courseIds.filter(id => id !== courseId);
            delete comparison.courses[courseId];

            StorageManager.setComparison(comparison);
            this.showNotification(`${removedCourse.name} removed from comparison`, 'success');
        },

        /**
         * Check if course is in comparison
         * @param {string} courseId
         * @returns {boolean}
         */
        isCourseSelected(courseId) {
            const comparison = StorageManager.getComparison();
            return comparison.courseIds.includes(courseId);
        },

        /**
         * Get current comparison count
         * @returns {number}
         */
        getCount() {
            const comparison = StorageManager.getComparison();
            return comparison.courseIds.length;
        },

        /**
         * Clear all courses from comparison
         */
        clearAll() {
            StorageManager.clearComparison();
            this.showNotification('All courses removed from comparison', 'success');
        },

        /**
         * Update all UI elements
         */
        updateUI() {
            this.updateButtons();
            this.updateCompareBar();
            this.updateComparisonTable();
        },

        /**
         * Update button states
         */
        updateButtons() {
            const buttons = StateManager.domElements.toggleButtons;
            buttons.forEach(button => {
                const courseId = button.dataset.id;
                const courseName = button.dataset.name?.trim() || 'this course';
                const isSelected = this.isCourseSelected(courseId);
                if (isSelected) {
                    button.innerHTML = `<span>Remove <span class="sr-only">${courseName} from courses to compare</span></span>  
                    <i class="fa-solid fa-circle-minus qld__icon qld__icon--md" aria-hidden="true" data-visible="onhover"></i>
                    <i class="fa-light fa-circle-minus qld__icon qld__icon--md" aria-hidden="true"></i>`;
                    button.classList.add('qld__btn--selected');
                    button.setAttribute('aria-pressed', 'true');
                } else {
                    button.innerHTML = `<span><span class="sr-only">Add ${courseName} to courses to</span> Compare</span>
                    <i class="fa-solid fa-circle-plus qld__icon qld__icon--md" aria-hidden="true" data-visible="onhover"></i>
                    <i class="fa-light fa-circle-plus qld__icon qld__icon--md" aria-hidden="true"></i>`;
                    button.classList.remove('qld__btn--selected');
                    button.setAttribute('aria-pressed', 'false');                    
                }
            });
        },

        /**
         * Get HTML for a course item in the compare bar
         * @param {string} courseName
         * @param {string} courseId
         * @returns {string}
         */
        getCourseItemHTML(courseName, courseId) {
            return `
                <li class="col-xs-12 col-md-6 col-lg-3 qld__card-list--matchheight qld__card-list--col">
                    <div class="qld__card qld__card--alt ">
                        <div class="qld__card__inner">
                            <div class="qld__card__content">
                                <div class="qld__card__content-inner">
                                    <p class="qld__card__description">${courseName}</p>
                                    <button class="qld__btn qld__btn--close" type="button" data-role="remove-course" data-course-id="${courseId}">
                                        <span class="sr-only"> Remove ${courseName} from courses to compare </span>
                                        <i class="fa-light fa-circle-minus qld__icon qld__icon--sm" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </li>`;
        },

        /**
         * Get HTML for an empty placeholder card
         * @returns {string}
         */
        getEmptyCardHTML() {
            return `
                <li class="col-xs-12 col-md-6 col-lg-3 qld__card-list--matchheight qld__card-list--col">
                    <div class="qld__card qld__card--alt qld__card--empty"></div>
                </li>`;
        },

        /**
         * Update floating compare bar
         */
        updateCompareBar() {
            const count = this.getCount();
            const compareBar = StateManager.domElements.comparisonDrawerContainer;

            if (!compareBar) return;

            const comparison = StorageManager.getComparison();

            // Show/hide compare bar based on count
            if (count > 0) {
                compareBar.classList.add('qld__compare-popup--visible');
                compareBar.setAttribute('aria-hidden', 'false');
                compareBar.inert = false;
            } else {
                compareBar.classList.remove('qld__compare-popup--visible');
                compareBar.setAttribute('aria-hidden', 'true');
                compareBar.inert = true;
            }

            // Update count displays
            const countElement = StateManager.domElements.countElement;
            if (countElement) {
                countElement.textContent = count;
            }

            const maxElement = StateManager.domElements.maxElement;
            if (maxElement) {
                maxElement.textContent = CONFIG.MAX_COURSES;
            }

            // Update course list
            const listElement = StateManager.domElements.courseList;
            if (listElement) {
                const cards = [];
                
                // Add course cards
                comparison.courseIds.forEach(courseId => {
                    const course = comparison.courses[courseId];
                    cards.push(this.getCourseItemHTML(course.name, course.id));
                });
                
                // Add empty placeholder
                const emptyCount = CONFIG.MAX_COURSES - count;
                for (let i = 0; i < emptyCount; i++) {
                    cards.push(this.getEmptyCardHTML());
                }
                
                listElement.innerHTML = cards.join('');

                // Attach remove event listeners
                listElement.querySelectorAll('[data-role="remove-course"]').forEach(button => {
                    button.addEventListener('click', () => {
                        const courseId = button.dataset.courseId;
                        this.removeCourse(courseId);
                    });
                });
            }
        },

        /**
         * Update comparison table (for comparison page)
         */
        updateComparisonTable() {
            const tableContainer = StateManager.domElements.comparisonTableContainer;
            if (!tableContainer) return;

            if (this.initialTableHTML === null) {
                this.initialTableHTML = tableContainer.innerHTML;
            }

            const comparison = StorageManager.getComparison();

            if (this.getCount() === 0) {
                tableContainer.innerHTML = this.initialTableHTML;
                return;
            }

            const coursesArray = comparison.courseIds.map(id => comparison.courses[id]);
            tableContainer.innerHTML = this.renderComparisonTable(coursesArray);
            this.attachTableEventListeners();
        },


        /**
         * Render comparison table HTML
         * @param {Course[]} courses
         * @returns {string}
         */
        renderComparisonTable(courses) {
            const bodyAttributes = [
                { key: 'summaryDuration', label: 'Estimated duration', type: 'text', toggleTipId: 'toggle-tip-compare-duration' },
                { key: 'training', label: 'On-the-job training', type: 'availability', toggleTipId: 'toggle-tip-compare-training' },
                { key: 'financialHelp', label: 'Financial help', type: 'availability', toggleTipId: 'toggle-tip-compare-funded' },
                { key: 'entryRequirements', label: 'Entry requirements', type: 'availability', toggleTipId: 'toggle-tip-compare-entry' },
                { key: 'online', label: 'Online', type: 'online-availability' },
                { key: 'partTime', label: 'Part-time', type: 'may-availability' },
                //{ key: 'occupationPrimary', label: 'You can become', type: 'text', extraClassname: 'capitalised' },
                { key: 'deliveryModes', label: 'Possible delivery modes', type: 'delivery-tags' }
            ];

            const footerAttributes = [
                { key: 'code', label: 'Course details', type: 'link' }
            ];

            let tableHTML = `
        <div id="qld__comparison-table__title" class="qld__table__caption"><h2>Course comparison<span class="qld__caption">${courses.length} ${courses.length === 1 ? 'course selected' : 'courses selected to compare'} </span></h2></div>
        <div class="qld__table--responsive qld__table--contained">
          <table class="qld__table ${CONFIG.tableStyling} qld__comparison-table" aria-labelledby="qld__comparison-table__title" data-column-count="${courses.length}">
            <thead>
              <tr>
                <th class="qld__table__header--width-10">&nbsp;</th>
                ${courses.map((course, index) => `
                  <th class="qld__comparison-table__course qld__table__header--width-20">
                      <div class="qld__course-name">${course.name}</div>
                      <button type="button" class="qld__btn qld__btn--close" 
                              data-role="remove-course" 
                              data-course-id="${course.id}">
                        <span class="sr-only">Remove ${course.name} from courses to compare</span>
                        <i class="fa-light fa-circle-minus qld__icon qld__icon--sm" aria-hidden="true"></i>
                      </button>
                  </th>
                `).join('')}
              </tr>
            </thead>
            <tbody>
              ${bodyAttributes.map(attr => `
                  <tr>
                    <td>
                      ${attr.label}
                      ${attr.toggleTipId ? `
                      <span class="qld__toggle-tip-trigger qld__toggle-tip-trigger-icon qld__icon--trail"
                        data-target="${attr.toggleTipId}"
                        role="button"
                        aria-expanded="false"
                        aria-label="Show help"
                        aria-controls="${attr.toggleTipId}"
                        tabindex="0"
                        ><svg class="qld__icon qld__icon--sm" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><use href="${CONFIG.extendedIconsUrl}#information"></use></svg></span>
                      ` : ''}
                    </td>
                    ${courses.map(course => `
                      <td class="qld__comparison-table__value${attr.extraClassname?' '+attr.extraClassname:''}">
                        ${this.formatValue(course, attr.key, attr.type)}
                      </td>
                    `).join('')}
                  </tr>
                `).join('')}
            </tbody>
            <tfoot>
              ${footerAttributes.map(attr => `
                  <tr>
                    <td class="qld__comparison-table__attribute">
                      ${attr.label}
                    </td>
                    ${courses.map(course => `
                      <td class="qld__comparison-table__value${attr.extraClassname?' '+attr.extraClassname:''}">
                        ${this.formatValue(course, attr.key, attr.type)}
                      </td>
                    `).join('')}
                  </tr>
                `).join('')}
            </tfoot>
          </table>
        </div>
      `;

            return tableHTML;
        },

        /**
         * Attach event listeners to table elements
         */
        attachTableEventListeners() {
            const tableContainer = StateManager.domElements.comparisonTableContainer;
            if (!tableContainer) return;
            
            // Remove course buttons
            tableContainer.querySelectorAll('[data-role="remove-course"]').forEach(button => {
                button.addEventListener('click', () => {
                    const courseId = button.dataset.courseId;
                    this.removeCourse(courseId);
                });
            });

            // Attach clear all event listener - removed but leaving code in case we reinstate
            const clearBtn = StateManager.domElements.clearButton;
            if (clearBtn) {
                clearBtn.addEventListener('click', () => {
                    this.clearAll();
                });
            }

            // Initialize toggle tip triggers for dynamically added content
            if (typeof window.initNewTriggers === 'function') {
                window.initNewTriggers(tableContainer);
            }
        },

        /**
         * Format value based on type
         * @param {Course} course - The course object
         * @param {string} key - The property key
         * @param {string} type - The format type
         * @returns {string}
         */
        formatValue(course, key, type) {
            switch (type) {
                case 'cost-range':
                    return this.formatCostRange(course.minFee, course.maxFee);
                case 'availability':
                    return this.formatAvailability(course[key]);
                case 'may-availability':
                    return this.formatMayAvailability(course[key]);
                case 'online-availability':
                    return this.formatOnlineAvailability(course.fullyOnline, course.partiallyOnline);
                case 'delivery-tags':
                    return this.formatDeliveryTags(course);
                case 'link':
                    return course.code ? `<a href="${this.formatCourseLink(course.code)}" class="qld__direction-link qld__direction-link--right" aria-label="${CONFIG.courseDetailsLinkText} - ${course.name}">${CONFIG.courseDetailsLinkText}</a>` : '–';
                case 'text':
                default:
                    return course[key] || '–';
            }
        },

        /**
         * Format cost range
         * @param {number} minFee
         * @param {number} maxFee
         * @returns {string}
         */
        formatCostRange(minFee, maxFee) {
            const formatCurrency = (value) => {
                return `$${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            };
            
            if (!minFee && !maxFee) {
                return '–';
            }
            
            return `${formatCurrency(minFee)} – ${formatCurrency(maxFee)}`;
        },

        /**
         * Format boolean availability
         * @param {boolean} value
         * @returns {string}
         */
        formatAvailability(value) {
            //if (typeof value !== 'boolean') return '–';
            return value 
                ? `<i class="fa-light fa-circle-check qld__icon qld__icon--lead qld__icon--sm qld__icon--success" aria-hidden="true"></i>${CONFIG.availableText}`
                : `<i class="fa-light fa-circle-xmark qld__icon qld__icon--lead qld__icon--sm qld__icon--unavailable" aria-hidden="true"></i>${CONFIG.notAvailableText}`;
        },

        /**
         * Format boolean availability using mayBeAvailableText for true values
         * @param {boolean} value
         * @returns {string}
         */
        formatMayAvailability(value) {
            return value 
                ? `<i class="fa-light fa-circle-check qld__icon qld__icon--lead qld__icon--sm qld__icon--success" aria-hidden="true"></i>${CONFIG.mayBeAvailableText}`
                : `<i class="fa-light fa-circle-xmark qld__icon qld__icon--lead qld__icon--sm qld__icon--unavailable" aria-hidden="true"></i>${CONFIG.notAvailableText}`;
        },

        /**
         * Format online availability with special handling for partially online
         * @param {boolean} fullyOnline
         * @param {boolean} partiallyOnline
         * @returns {string}
         */
        formatOnlineAvailability(fullyOnline, partiallyOnline) {
            const isAvailable = fullyOnline || partiallyOnline;
            const iconClass = isAvailable ? 'fa-circle-check qld__icon--success' : 'fa-circle-xmark qld__icon--unavailable';
            const text = fullyOnline ? CONFIG.mayBeAvailableText : (partiallyOnline ? `Partially online ${CONFIG.mayBeAvailableText.toLowerCase()}` : CONFIG.notAvailableText);
            
            return `<i class="fa-light ${iconClass} qld__icon qld__icon--lead qld__icon--sm" aria-hidden="true"></i>${text}`;
        },

        /**
         * Format delivery mode tags (excluding fully online, partially online, and part-time)
         * @param {Course} course
         * @returns {string}
         */
        formatDeliveryTags(course) {
            const deliveryModes = [
                { key: 'workplaceBased', label: 'Workplace-based' },
                { key: 'classroomLearning', label: 'Some classroom learning' },
                { key: 'practicalLearning', label: 'Practical learning' },
                { key: 'classroomBased', label: 'Classroom-based' }
            ];
            
            const tags = deliveryModes
                .filter(mode => course[mode.key])
                .map(mode => `<li class="qld__tag qld__tag--logistics">${mode.label}</li>`);
            
            return tags.length > 0 
                ? `<ul class="qld__tag-list">${tags.join('')}</ul>` 
                : '–';
        },
        
        /**
         * Format course detail page link from course code
         * @param {string} courseCode - The course code
         * @returns {string} The full URL to the course detail page
         */
        formatCourseLink(courseCode) {
            if (!courseCode) return '';
            const base = CONFIG.courseRouteBaseUrl.replace(/\/*$/, '/');
            return `${base}${encodeURIComponent(courseCode)}`;
        },

        /**
         * Show notification to user
         * @param {string} message
         * @param {string} type - 'success', 'warning', 'info', 'error'
         */
        showNotification(message, type = 'info') {
            //console.log(type, message);
            return;
        }        
    };

    // ======================
    // Event Handlers
    // ======================

    /**
     * Handle course toggle button click
     * @param {Event} event
     */
    function handleCourseToggle(event) {
        const button = event.currentTarget;
        const courseId = button.dataset.id;

        if (ComparisonManager.isCourseSelected(courseId)) {
            ComparisonManager.removeCourse(courseId);
            cancelActiveFlyToAnimation();
        } else {
            const courseData = ComparisonManager.extractCourseData(button);
            if (ComparisonManager.addCourse(courseData, button)) {
                animateCourseToCompare(button);
            }

        }
    }

    function animateCourseToCompare(button) {
        cancelActiveFlyToAnimation();
        if (prefersReducedMotion.matches) return;

        const sourceCard = button.closest('.qld__card,.qld__banner__wrapper');
        const drawer = StateManager.domElements.comparisonDrawerContainer;
        if (!sourceCard || !drawer) return;

        const target = drawer;
        const cardRect = sourceCard.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        // Clone the card
        const ghost = sourceCard.cloneNode(true);
        ghost.classList.add('qld__card','qld__card--ghost');

        // Remove everything except the heading section
        const selectors = [
            '.courseDescription',
            '.courseInfo',
            '.qld__horizontal-rule',
            '.qld__card__footer',
            '.qld__breadcrumbs',
            '.qld__btn'
        ];
        ghost.querySelectorAll(selectors.join(', ')).forEach(el => el.remove());

        Object.assign(ghost.style, {
            position: 'fixed',
            left: `${cardRect.left}px`,
            top: `${cardRect.top}px`,
            width: `${cardRect.width}px`,
            margin: 0,
            zIndex: 9999,
            pointerEvents: 'none',
            transformOrigin: 'center center',
            boxShadow: '0 20px 40px rgba(0,0,0,.18)',
            overflow: 'hidden'
        });

        document.body.appendChild(ghost);

        const startX = cardRect.left;
        const startY = cardRect.top;

        const endX = targetRect.left + (targetRect.width / 2) - (cardRect.width / 2);
        const endY = targetRect.top;

        const dx = endX - startX;
        const dy = endY - startY;
        
        const animation = ghost.animate([
            {
                transform: 'translate(0,0) scale(1)',
                opacity: 1
            },
            {
                transform: `translate(${dx * 0.35}px, ${dy * 0.35}px) scale(.85)`,
                opacity: 1,
                offset: 0.35
            },
            {
                transform: `translate(${dx}px, ${dy}px) scale(.25)`,
                opacity: 0
            }
        ], {
            duration: CONFIG.flyToDuration,
            easing: 'cubic-bezier(.22,1,.36,1)',
            fill: 'forwards'
        });

        activeFlyToGhost = ghost;
        activeFlyToAnimation = animation;

        animation.finished
            .then(() => {
                if (activeFlyToGhost === ghost) {
                    activeFlyToGhost = null;
                    activeFlyToAnimation = null;
                }
                ghost.remove();
            })
            .catch(() => {
                if (activeFlyToGhost === ghost) {
                    activeFlyToGhost = null;
                    activeFlyToAnimation = null;
                }
                ghost.remove();
            });
    }


    function cancelActiveFlyToAnimation() {
        if (activeFlyToAnimation) {
            activeFlyToAnimation.cancel();
            activeFlyToAnimation = null;
        }

        if (activeFlyToGhost) {
            activeFlyToGhost.remove();
            activeFlyToGhost = null;
        }
    }

    // ======================
    // Initialization
    // ======================
        function  renderToggleTip() {
            const id = CONFIG.removeNoteToggleTipId;
            if (document.getElementById(id)) {
                return;
            }
            
            const wrapper = document.createElement('div');
            wrapper.className = 'qld__toggle-tip';
            wrapper.id = id;
            
            const content = document.createElement('div');
            content.className = 'qld__toggle-tip-content qld__toggle-tip-hidden qld__toggle-tip-aligned-top';
            content.setAttribute('role', 'status');
            content.textContent = CONFIG.removeNoteText;
            
            const carat = document.createElement('div');
            carat.className = 'qld__toggle-tip-content-carat qld__toggle-tip-hidden';
            
            wrapper.appendChild(content);
            wrapper.appendChild(carat);
            
            const section = document.createElement('section');
            section.className = 'qld__body qld__body--collapsed';
            section.appendChild(wrapper);
            
            const mainElement = document.querySelector('main') || document.body;
            mainElement.appendChild(section);
        }

    /**
     * Initialize the course comparison system
     */
    function initCourseComparison() {
        renderToggleTip();
        StateManager.init();
        
        // Attach document event listener to handle dynamic DOM changes to the buttons
        document.addEventListener('click', (event) => {
            ComparisonManager.closeAllToggleTips();
            const button = event.target.closest('[data-role="course-toggle"]');
            if (button) {
                handleCourseToggle({ currentTarget: button });
            }
        });

        // Close toggle tips when user presses Escape key
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                ComparisonManager.closeAllToggleTips();
            }
        });

        // Close toggle tips when focus leaves a course toggle button
        document.addEventListener('focusout', (event) => {
            const button = event.target.closest('[data-role="course-toggle"]');
            if (button) {
                ComparisonManager.closeAllToggleTips();
            }
        });

        // Listen for comparison changes
        window.addEventListener('comparisonChanged', () => {
            ComparisonManager.updateUI();
        });

        // Listen for storage changes from other tabs/pages
        window.addEventListener('storage', (event) => {
            if (event.key === CONFIG.STORAGE_KEY) {
                StorageManager.invalidateCache(); // keep cache in sync
                ComparisonManager.updateUI();
            }
        });

        // Initial UI update
        ComparisonManager.updateUI();

        // Handle browser back/forward buttons
        window.addEventListener('pageshow', () => {
            ComparisonManager.updateUI();
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initCourseComparison);
    } else {
        initCourseComparison();
    }


})();